import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useUser } from '../../contexts/UserContext';
import { useAuth0 } from '@auth0/auth0-react';
import { PremiumFeatureDialog } from '../PremiumFeatureDialog';
import { useAnalytics } from '../../hooks/useAnalytics';
import { getSmartReturnTo } from '../../utils/auth';
import { EDGE_GREEN, EDGE_GREEN_HOVER } from '../../theme/edge';

// Sportsbook brand colors
const SPORTSBOOK_COLORS = {
  fanduel: {
    primary: '#1493FF',
    hover: '#0D7FE8',
    text: '#FFFFFF'
  },
  draftkings: {
    primary: '#53D337',
    hover: '#45B82E',
    text: '#000000'
  }
};

// Sportsbook abbreviations for compact display
const SPORTSBOOK_ABBREV = {
  fanduel: 'FD',
  draftkings: 'DK'
};

/**
 * TapToBetButton - A premium feature that allows users to bet directly on sportsbooks
 * 
 * @param {string} bookmaker - 'fanduel' or 'draftkings'
 * @param {string} deepLinkUrl - The URL to open the sportsbook app/site
 * @param {string} variant - 'icon' (compact) or 'button' (full)
 * @param {number} ev - The EV percentage (used to highlight +EV bets)
 * @param {number} edge - The Edge value (points of value vs the book, optional)
 * @param {function} onBetClick - Callback when bet is clicked (for analytics)
 * @param {string} label - Custom label text (defaults to bookmaker abbreviation)
 * @param {boolean} useEvColor - If true, button color changes based on EV/Edge (green for positive, white for negative)
 * @param {string} sport - Sport name for analytics (e.g., 'nba', 'nfl')
 * @param {string} betType - Bet type for analytics (e.g., 'moneyline', 'spread', 'total')
 * @param {string} pickId - Pick ID for analytics
 */
const TapToBetButton = ({ 
  bookmaker, 
  deepLinkUrl, 
  variant = 'icon',
  ev = 0,
  edge = null,
  onBetClick,
  disabled = false,
  label = null,
  useEvColor = false,
  sport = null,
  betType = null,
  pickId = null
}) => {
  const theme = useTheme();
  const { status, isPremium, isLoading } = useUser();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { trackTapToBetClicked, trackTapToBetBlocked, trackTapToBetLoginRedirect } = useAnalytics();
  const userIsPremium = isPremium();
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Extract sport from pathname if not provided
  const getSportFromPath = () => {
    if (sport) return sport;
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/nba')) return 'nba';
    if (path.includes('/nfl')) return 'nfl';
    if (path.includes('/mlb')) return 'mlb';
    if (path.includes('/nhl')) return 'nhl';
    if (path.includes('/ncaaf')) return 'ncaaf';
    if (path.includes('/mens-cbb') || path.includes('/menscbb')) return 'menscbb';
    return 'unknown';
  };
  
  // Extract bet type from pathname if not provided
  const getBetTypeFromPath = () => {
    if (betType) return betType;
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/moneyline')) return 'moneyline';
    if (path.includes('/spread') || path.includes('/run-line')) return 'spread';
    if (path.includes('/total') || path.includes('/totals')) return 'total';
    return 'unknown';
  };
  
  // Don't show locked state while loading - wait for premium status to be determined
  if (isLoading) {
    return null; // or a loading placeholder
  }
  
  const defaultColors = SPORTSBOOK_COLORS[bookmaker?.toLowerCase()] || SPORTSBOOK_COLORS.fanduel;
  const defaultAbbrev = SPORTSBOOK_ABBREV[bookmaker?.toLowerCase()] || bookmaker?.toUpperCase()?.slice(0, 2);
  // Ensure ev is a number, default to 0 if not
  const evNumber = typeof ev === 'number' ? ev : (typeof ev === 'string' ? parseFloat(ev) || 0 : 0);
  // Ensure edge is a number if provided
  const edgeNumber = edge !== null && edge !== undefined ? (typeof edge === 'number' ? edge : (typeof edge === 'string' ? parseFloat(edge) || 0 : 0)) : null;
  // Consider both EV and Edge for positive value - green if either is positive
  const hasPositiveValue = evNumber > 0 || (edgeNumber !== null && edgeNumber > 0);
  
  // Determine button colors and label
  let colors = defaultColors;
  let abbrev = label || defaultAbbrev;
  
  if (useEvColor && hasPositiveValue) {
    // The +EV system green (#190, #227): solid for a positive edge.
    colors = {
      primary: EDGE_GREEN,
      hover: EDGE_GREEN_HOVER,
      text: '#FFFFFF'
    };
  } else if (useEvColor && !hasPositiveValue) {
    // White/grey for negative or zero EV/Edge
    colors = {
      primary: '#FFFFFF',
      hover: theme.palette.grey[100],
      text: theme.palette.text.primary
    };
  }
  
  const handleClick = (e) => {
    const currentSport = getSportFromPath();
    const currentBetType = getBetTypeFromPath();
    const evNumber = typeof ev === 'number' ? ev : (typeof ev === 'string' ? parseFloat(ev) || 0 : 0);
    
    if (!userIsPremium || !deepLinkUrl) {
      e.preventDefault();
      e.stopPropagation();
      
      // If not authenticated, show login first
      if (!isAuthenticated) {
        // Track login redirect
        trackTapToBetLoginRedirect(
          bookmaker || 'unknown',
          evNumber,
          currentBetType,
          currentSport,
          window.location.pathname
        );
        
        loginWithRedirect({
          appState: { returnTo: getSmartReturnTo() }
        });
        return;
      }
      
      // If authenticated but not premium, show upgrade dialog
      trackTapToBetBlocked(
        bookmaker || 'unknown',
        evNumber,
        currentBetType,
        currentSport,
        'dialog_shown',
        {
          user_tier: status,
          pick_id: pickId,
          deep_link_url: deepLinkUrl
        }
      );
      
      setDialogOpen(true);
      return;
    }
    
    // Premium user - track successful click
    trackTapToBetClicked(
      bookmaker || 'unknown',
      evNumber,
      currentBetType,
      currentSport,
      {
        user_tier: 'premium',
        deep_link_url: deepLinkUrl,
        pick_id: pickId
        // Note: betDescription is not available in TapToBetButton component
        // It's only available in TapToBetHighlight component
      }
    );
    
    // Track the bet click for analytics (legacy callback)
    if (onBetClick) {
      onBetClick({
        bookmaker,
        ev,
        deepLinkUrl
      });
    }
    
    // Open the deep link in a new tab
    window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');
  };
  
  // Premium user with valid deep link - show the bet button
  if (userIsPremium && deepLinkUrl && !disabled) {
    if (variant === 'icon') {
      return (
        <Tooltip 
          title={`Bet on ${bookmaker === 'fanduel' ? 'FanDuel' : 'DraftKings'}${hasPositiveValue ? ` (+${evNumber.toFixed(1)}% EV${edgeNumber !== null && edgeNumber > 0 ? `, +${edgeNumber.toFixed(1)} Edge` : ''})` : ''}`}
          arrow
        >
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              bgcolor: colors.primary,
              color: colors.text,
              width: 24,
              height: 24,
              fontSize: '0.65rem',
              fontWeight: 700,
              borderRadius: 1,
              border: useEvColor && !hasPositiveValue ? `1px solid ${theme.palette.grey[300]}` : 'none',
              '&:hover': {
                bgcolor: colors.hover,
                transform: 'scale(1.05)'
              },
              transition: 'all 0.15s ease',
              boxShadow: 'none'
            }}
          >
            {abbrev}
          </IconButton>
        </Tooltip>
      );
    }
    
    // Full button variant
    return (
      <Box
        component="button"
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: colors.primary,
          color: colors.text,
          border: 'none',
          borderRadius: 2,
          px: 2,
          py: 1,
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '0.875rem',
          '&:hover': {
            bgcolor: colors.hover,
            transform: 'translateY(-1px)'
          },
          transition: 'all 0.15s ease',
          boxShadow: hasPositiveValue 
            ? `0 2px 8px ${colors.primary}60, 0 0 0 2px ${theme.palette.success.main}` 
            : '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <span>Bet on {bookmaker === 'fanduel' ? 'FanDuel' : 'DraftKings'}</span>
        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
      </Box>
    );
  }
  
  // Non-premium user or no deep link - show locked state
  if (variant === 'icon') {
    return (
      <>
        <Box
          onClick={handleClick}
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: theme.palette.grey[300]
            }
          }}
        >
          <LockIcon sx={{ fontSize: '0.9rem', color: theme.palette.grey[500] }} />
        </Box>
        <PremiumFeatureDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          featureName="Tap to Bet"
        />
      </>
    );
  }
  
  // Full button locked variant
  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: theme.palette.grey[200],
          color: theme.palette.grey[600],
          borderRadius: 2,
          px: 2,
          py: 1,
          cursor: 'pointer',
          fontWeight: 500,
          fontSize: '0.875rem',
          '&:hover': {
            bgcolor: theme.palette.grey[300]
          }
        }}
      >
        <LockIcon sx={{ fontSize: '1rem' }} />
        <span>Tap to Bet</span>
      </Box>
      <PremiumFeatureDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        featureName="Tap to Bet"
      />
    </>
  );
};

/**
 * TapToBetCell - A compact cell for use in tables showing both sportsbook options
 * 
 * @param {object} fanduelData - { deepLinkUrl, ev }
 * @param {object} draftkingsData - { deepLinkUrl, ev }
 * @param {function} onBetClick - Callback for analytics
 */
export const TapToBetCell = ({ fanduelData, draftkingsData, onBetClick }) => {
  // If user is premium and has at least one deep link, show the buttons
  const hasFanduel = fanduelData?.deepLinkUrl;
  const hasDraftkings = draftkingsData?.deepLinkUrl;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 0.5,
      alignItems: 'center'
    }}>
      {/* FanDuel button */}
      <TapToBetButton
        bookmaker="fanduel"
        deepLinkUrl={fanduelData?.deepLinkUrl}
        ev={fanduelData?.ev || 0}
        variant="icon"
        onBetClick={onBetClick}
        disabled={!hasFanduel}
      />
      
      {/* DraftKings button */}
      <TapToBetButton
        bookmaker="draftkings"
        deepLinkUrl={draftkingsData?.deepLinkUrl}
        ev={draftkingsData?.ev || 0}
        variant="icon"
        onBetClick={onBetClick}
        disabled={!hasDraftkings}
      />
    </Box>
  );
};

/**
 * TapToBetHighlight - A prominent button for the best bet opportunity
 * Used on cards and dashboard for the recommended action
 * 
 * @param {string} bookmaker - 'fanduel' or 'draftkings'
 * @param {string} deepLinkUrl - The URL to open
 * @param {number} ev - The EV percentage
 * @param {string} betDescription - e.g., "Lakers +3.5 (-110)"
 */
export const TapToBetHighlight = ({ 
  bookmaker, 
  deepLinkUrl, 
  ev, 
  betDescription,
  onBetClick,
  isDemo = false,
  sport = null,
  betType = null,
  pickId = null
}) => {
  const theme = useTheme();
  const { status, isPremium } = useUser();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { trackTapToBetClicked, trackTapToBetBlocked, trackTapToBetLoginRedirect } = useAnalytics();
  const userIsPremium = isPremium();
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const colors = SPORTSBOOK_COLORS[bookmaker?.toLowerCase()] || SPORTSBOOK_COLORS.fanduel;
  const sportsbookName = bookmaker === 'fanduel' ? 'FanDuel' : 'DraftKings';
  
  // Extract sport from pathname if not provided
  const getSportFromPath = () => {
    if (sport) return sport;
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/nba')) return 'nba';
    if (path.includes('/nfl')) return 'nfl';
    if (path.includes('/mlb')) return 'mlb';
    if (path.includes('/nhl')) return 'nhl';
    if (path.includes('/ncaaf')) return 'ncaaf';
    if (path.includes('/mens-cbb') || path.includes('/menscbb')) return 'menscbb';
    return 'unknown';
  };
  
  // Extract bet type from pathname if not provided
  const getBetTypeFromPath = () => {
    if (betType) return betType;
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/moneyline')) return 'moneyline';
    if (path.includes('/spread') || path.includes('/run-line')) return 'spread';
    if (path.includes('/total') || path.includes('/totals')) return 'total';
    return 'unknown';
  };
  
  const handleClick = () => {
    if (isDemo) return;

    const currentSport = getSportFromPath();
    const currentBetType = getBetTypeFromPath();
    const evNumber = typeof ev === 'number' ? ev : (typeof ev === 'string' ? parseFloat(ev) || 0 : 0);

    if (!userIsPremium || !deepLinkUrl) {
      // If not authenticated, show login first
      if (!isAuthenticated) {
        trackTapToBetLoginRedirect(
          bookmaker || 'unknown',
          evNumber,
          currentBetType,
          currentSport,
          window.location.pathname
        );
        
        loginWithRedirect({
          appState: { returnTo: getSmartReturnTo() }
        });
        return;
      }
      
      // If authenticated but not premium, show upgrade dialog
      trackTapToBetBlocked(
        bookmaker || 'unknown',
        evNumber,
        currentBetType,
        currentSport,
        'dialog_shown',
        {
          user_tier: status,
          pick_id: pickId,
          deep_link_url: deepLinkUrl,
          bet_description: betDescription
        }
      );
      
      setDialogOpen(true);
      return;
    }
    
    // Premium user - track successful click
    trackTapToBetClicked(
      bookmaker || 'unknown',
      evNumber,
      currentBetType,
      currentSport,
      {
        user_tier: 'premium',
        deep_link_url: deepLinkUrl,
        pick_id: pickId,
        bet_description: betDescription
      }
    );
    
    if (onBetClick) {
      onBetClick({ bookmaker, ev, deepLinkUrl, betDescription });
    }
    
    window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');
  };
  
  if ((userIsPremium || isDemo) && deepLinkUrl) {
    return (
      <Box
        component="button"
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          bgcolor: colors.primary,
          color: colors.text,
          border: 'none',
          borderRadius: 2,
          py: 1.5,
          px: 2,
          cursor: 'pointer',
          fontWeight: 700,
          fontSize: '1rem',
          '&:hover': {
            bgcolor: colors.hover,
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${colors.primary}50`
          },
          transition: 'all 0.2s ease',
          boxShadow: `0 2px 8px ${colors.primary}40`
        }}
      >
        <span>Bet on {sportsbookName}</span>
        <OpenInNewIcon sx={{ fontSize: '1.1rem' }} />
      </Box>
    );
  }
  
  // Locked state - show upgrade prompt (or demo state - non-interactive)
  return (
    <>
      <Box
        onClick={isDemo ? undefined : handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          bgcolor: theme.palette.grey[100],
          color: theme.palette.grey[600],
          borderRadius: 2,
          py: 1.5,
          px: 2,
          cursor: isDemo ? 'default' : 'pointer',
          pointerEvents: isDemo ? 'none' : 'auto',
          fontWeight: 600,
          fontSize: '1rem',
          border: `1px dashed ${theme.palette.grey[400]}`,
          '&:hover': isDemo ? {} : {
            bgcolor: theme.palette.grey[200],
            borderColor: theme.palette.primary.main
          },
          transition: 'all 0.2s ease'
        }}
      >
        <LockIcon sx={{ fontSize: '1.1rem' }} />
        <span>Tap to Bet</span>
      </Box>
      {!isDemo && (
        <PremiumFeatureDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          featureName="Tap to Bet"
        />
      )}
    </>
  );
};

export default TapToBetButton;

